// frontend/src/widgets/ResourceRolesWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Updated:      2026-09-09 15:01 BST
//
// Description:
//   Links a Resource to a User and assigns a System Role.
//   - Populates Resources from /api/resources
//   - Populates Roles from /api/roles/
//   - Populates Users from /api/users/
//   - PUT /api/resources/{id} for { user_id, system_role }
//   - Supports creating a new user, then auto-links it.
//
// AUD8 fix:
//   - Adds explicit Resource selector inside the widget.
//   - Removes misleading "top-right" instruction.
//   - Supports mapping the logged-in user to a selected resource.
//   - Sends only editable fields to backend. Never sends tenant_id.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves all resource/user/role loading and save/create/link handlers.
//   - Resource selector remains full-width.
//   - User + Save Link + Link Me layout collapses to one column on mobile.
//   - System Role + Save Role layout collapses to one column on mobile.
//   - Save Link + Role and Create User + Link become full-width touch targets.
//   - New-user fields collapse from two columns to one on mobile.
//   - Form controls become 44px touch targets on mobile while retaining the
//     existing desktop control height.
//   - Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary root sizing;
//       measure the complete natural form content;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the complete form content so conditional linked-user
//     content or validation/state changes cannot leave the widget clipped.
//
// Operational Notes:
//   - No API routes, payloads, callbacks, role semantics or AUD8 tenant rules changed.
//   - No tenant_id is sent.
//   - One render tree is shared by desktop and mobile.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAuth } from "../context/AuthContext";

const ResourceRolesWidget = ({ resource, onLinked, onRoleSaved }) => {
  const { theme } = useTheme();
  const { user } = useAuth();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);

  const [resources, setResources] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedResourceId, setSelectedResourceId] = useState(resource?.id || "");
  const [selectedUserId, setSelectedUserId] = useState(resource?.user_id || "");
  const [selectedRole, setSelectedRole] = useState(resource?.system_role || "");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    role: "Operator",
    status: "active",
    is_active: true,
    is_admin: false,
  });

  const selectedResource = useMemo(() => {
    return resources.find((r) => Number(r.id) === Number(selectedResourceId)) || resource || null;
  }, [resources, selectedResourceId, resource]);

  const selectedUser = useMemo(() => {
    return users.find((u) => Number(u.id) === Number(selectedUserId)) || null;
  }, [users, selectedUserId]);

  const label = useMemo(
    () => ({
      fontSize: 13,
      fontWeight: 700,
      color: "#333",
    }),
    []
  );

  const input = useMemo(
    () => ({
      height: 34,
      padding: "6px 8px",
      border: "1px solid #ccc",
      borderRadius: 6,
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      fontFamily: theme?.font_body || "inherit",
      marginTop: 6,
    }),
    [theme?.font_body]
  );

  const btnGeneral = useMemo(
    () => ({
      background: "var(--btn-general-normal, #1a1a1a)",
      color: "#fff",
      padding: "8px 12px",
      border: "none",
      borderRadius: 8,
      cursor: saving ? "not-allowed" : "pointer",
      fontWeight: 700,
      opacity: saving ? 0.7 : 1,
      whiteSpace: "nowrap",
    }),
    [saving]
  );

  const btnAction = useMemo(
    () => ({
      background: "var(--btn-action-normal, #e63946)",
      color: "#fff",
      padding: "8px 12px",
      border: "none",
      borderRadius: 8,
      cursor: saving ? "not-allowed" : "pointer",
      fontWeight: 700,
      opacity: saving ? 0.7 : 1,
      whiteSpace: "nowrap",
    }),
    [saving]
  );

  const loadData = async () => {
    setLoading(true);

    try {
      const [resourcesRes, rolesRes, usersRes] = await Promise.all([
        api.get("/resources"),
        api.get("/roles/"),
        api.get("/users/?limit=1000"),
      ]);

      const loadedResources = Array.isArray(resourcesRes.data) ? resourcesRes.data : [];
      const loadedRoles = Array.isArray(rolesRes.data) ? rolesRes.data : [];
      const loadedUsers = Array.isArray(usersRes.data) ? usersRes.data : [];

      setResources(loadedResources);
      setRoles(loadedRoles);
      setUsers(loadedUsers);

      if (!selectedResourceId && resource?.id) {
        setSelectedResourceId(resource.id);
      }

      if (!selectedResourceId && !resource?.id && loadedResources.length === 1) {
        setSelectedResourceId(loadedResources[0].id);
        setSelectedUserId(loadedResources[0].user_id || "");
        setSelectedRole(loadedResources[0].system_role || "");
      }
    } catch (e) {
      console.error("Resource role widget load failed:", e?.response?.data || e?.message || e);
      setResources([]);
      setRoles([]);
      setUsers([]);
      alert("Failed to load resources, users or roles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resource?.id) return;

    setSelectedResourceId(resource.id);
    setSelectedUserId(resource.user_id || "");
    setSelectedRole(resource.system_role || "");
  }, [resource?.id, resource?.user_id, resource?.system_role]);

  const handleResourceChange = (resourceId) => {
    setSelectedResourceId(resourceId);

    const nextResource = resources.find((r) => Number(r.id) === Number(resourceId));
    setSelectedUserId(nextResource?.user_id || "");
    setSelectedRole(nextResource?.system_role || "");
  };

  const saveResourcePatch = async (patch) => {
    if (!selectedResourceId) {
      alert("Select a resource first.");
      return false;
    }

    setSaving(true);

    try {
      await api.put(`/resources/${selectedResourceId}`, patch);
      await loadData();

      if (onLinked) onLinked();
      if (onRoleSaved) onRoleSaved();

      return true;
    } catch (e) {
      console.error("Resource update failed:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Resource update failed.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveRole = async () => {
    const ok = await saveResourcePatch({
      system_role: selectedRole || null,
    });

    if (ok) alert("Role saved.");
  };

  const linkUser = async () => {
    if (!selectedUserId) {
      alert("Choose a user to link.");
      return;
    }

    const ok = await saveResourcePatch({
      user_id: Number(selectedUserId),
    });

    if (ok) alert("User linked.");
  };

  const linkCurrentUser = async () => {
    if (!user?.id) {
      alert("Current user is not available.");
      return;
    }

    setSelectedUserId(user.id);

    const ok = await saveResourcePatch({
      user_id: Number(user.id),
    });

    if (ok) alert("Current user linked to selected resource.");
  };

  const saveLinkAndRole = async () => {
    if (!selectedUserId) {
      alert("Choose a user to link.");
      return;
    }

    const ok = await saveResourcePatch({
      user_id: Number(selectedUserId),
      system_role: selectedRole || null,
    });

    if (ok) alert("User link and role saved.");
  };

  const createAndLinkUser = async () => {
    if (!selectedResourceId) {
      alert("Select a resource first.");
      return;
    }

    const { username, email, first_name, last_name, password, role, status, is_active, is_admin } = newUser;

    if (!username || !email || !first_name || !last_name || !password) {
      alert("Fill in Username, Email, First Name, Last Name and Temporary Password.");
      return;
    }

    setSaving(true);

    try {
      const createdRes = await api.post("/users/", {
        username,
        email,
        first_name,
        last_name,
        password,
        role: role || "Operator",
        status: status || "active",
        is_active: is_active !== false,
        is_admin: Boolean(is_admin),
      });

      const created = createdRes?.data;

      await api.put(`/resources/${selectedResourceId}`, {
        user_id: created?.id,
        system_role: role || selectedRole || "Operator",
      });

      setSelectedUserId(created?.id || "");
      setSelectedRole(role || selectedRole || "Operator");

      await loadData();

      if (onLinked) onLinked();
      if (onRoleSaved) onRoleSaved();

      alert("User created and linked.");
    } catch (e) {
      console.error("Create/link user failed:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Create/link user failed.");
    } finally {
      setSaving(false);
    }
  };

  const selectedResourceLabel = selectedResource
    ? `${selectedResource.first_name || ""} ${selectedResource.last_name || ""}`.trim() ||
      selectedResource.email ||
      `Resource #${selectedResource.id}`
    : "No resource selected";

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const naturalContentHeight = Math.ceil(
          Math.max(
            content.getBoundingClientRect().height || 0,
            content.scrollHeight || 0
          )
        );

        if (
          Number.isFinite(naturalContentHeight) &&
          naturalContentHeight > 0
        ) {
          root.style.height = "auto";
          root.style.minHeight = `${naturalContentHeight}px`;
        } else {
          root.style.height = "auto";
          root.style.minHeight = "0px";
        }

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    loading,
    resources.length,
    roles.length,
    saving,
    selectedResourceId,
    selectedUserId,
    users.length,
  ]);

  if (loading) {
    return <div style={{ color: "#777", fontSize: 13 }}>Loading resource role controls...</div>;
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-resource-role-control {
            height: 44px !important;
            min-height: 44px !important;
          }

          .vx-resource-role-button {
            width: 100% !important;
            min-height: 44px;
            white-space: normal !important;
          }
        }
      `}</style>

      <div ref={contentRef} className="min-w-0">
        <div style={{ marginBottom: 12 }}>
        <label style={label}>Resource</label>
        <select
          value={selectedResourceId || ""}
          onChange={(e) => handleResourceChange(e.target.value)}
          className="vx-resource-role-control"
          style={input}
        >
          <option value="">Select resource...</option>
          {resources.map((r) => {
            const name =
              `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
              r.email ||
              `Resource #${r.id}`;

            return (
              <option key={r.id} value={r.id}>
                {name}
                {r.user_id ? ` — linked to user #${r.user_id}` : " — not linked"}
              </option>
            );
          })}
        </select>
      </div>

      <div style={{ marginBottom: 10, color: "#555" }}>
        <strong>Selected Resource:</strong> {selectedResourceLabel}
      </div>

      {selectedUser ? (
        <div style={{ marginBottom: 10, color: "#555", fontSize: 13 }}>
          <strong>Linked User:</strong>{" "}
          {`${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim() ||
            selectedUser.username ||
            selectedUser.email}{" "}
          ({selectedUser.email})
        </div>
      ) : (
        <div style={{ marginBottom: 10, color: "#777", fontSize: 13 }}>
          No user currently linked to this resource.
        </div>
      )}

      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div>
          <label style={label}>User</label>
          <select
            value={selectedUserId || ""}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="vx-resource-role-control"
          style={input}
          >
            <option value="">(none)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email} — {u.email}
              </option>
            ))}
          </select>
        </div>

        <div style={{ alignSelf: "end" }}>
          <button
            onClick={linkUser}
            className="vx-resource-role-button"
            style={btnGeneral}
            disabled={saving}
            title="Link selected user to selected resource"
          >
            Save Link
          </button>
        </div>

        <div style={{ alignSelf: "end" }}>
          <button
            onClick={linkCurrentUser}
            className="vx-resource-role-button"
            style={btnAction}
            disabled={saving}
            title="Link the currently logged-in user to selected resource"
          >
            Link Me
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <label style={label}>System Role</label>
          <select
            value={selectedRole || ""}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="vx-resource-role-control"
          style={input}
          >
            <option value="">(none)</option>
            {roles.map((r) => (
              <option key={r.id || r.name} value={r.name || r.id}>
                {r.name || r.id}
              </option>
            ))}
          </select>
        </div>

        <div style={{ alignSelf: "end" }}>
          <button
            onClick={saveRole}
            className="vx-resource-role-button"
            style={btnGeneral}
            disabled={saving}
            title="Persist role on selected resource"
          >
            Save Role
          </button>
        </div>
      </div>

      <div className="mt-3 flex justify-stretch md:justify-end">
        <button
          onClick={saveLinkAndRole}
          className="vx-resource-role-button"
          style={btnAction}
          disabled={saving}
          title="Save selected user and selected role together"
        >
          Save Link + Role
        </button>
      </div>

      <div style={{ borderTop: "1px solid #eee", margin: "14px 0" }} />

      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <div>
          <label style={label}>Username</label>
          <input
            type="text"
            value={newUser.username}
            onChange={(e) => setNewUser((p) => ({ ...p, username: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
            placeholder="janedoe001"
          />
        </div>

        <div>
          <label style={label}>Email</label>
          <input
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
            placeholder="name@company.co.uk"
          />
        </div>

        <div>
          <label style={label}>First Name</label>
          <input
            type="text"
            value={newUser.first_name}
            onChange={(e) => setNewUser((p) => ({ ...p, first_name: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
          />
        </div>

        <div>
          <label style={label}>Last Name</label>
          <input
            type="text"
            value={newUser.last_name}
            onChange={(e) => setNewUser((p) => ({ ...p, last_name: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
          />
        </div>

        <div>
          <label style={label}>Temporary Password</label>
          <input
            type="password"
            value={newUser.password}
            onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
            placeholder="••••••••"
          />
        </div>

        <div>
          <label style={label}>New User Role</label>
          <select
            value={newUser.role}
            onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
            className="vx-resource-role-control"
          style={input}
          >
            {roles.map((r) => (
              <option key={r.id || r.name} value={r.name || r.id}>
                {r.name || r.id}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 flex justify-stretch md:justify-end">
        <button
          onClick={createAndLinkUser}
          className="vx-resource-role-button"
          style={btnAction}
          disabled={saving}
          title="Create user and link to selected resource"
        >
          Create User + Link
        </button>
      </div>

        <div
          className="break-words"
          style={{
            color: "#777",
            fontSize: 12,
            marginTop: 8,
            overflowWrap: "anywhere",
          }}
        >
          For quote/opportunity creation, the logged-in user must be linked to an active resource in the same tenant.
        </div>
      </div>
    </div>
  );
};

export default ResourceRolesWidget;